import { Patient } from '@/core/models/patient.model'
import { useGetPatientQuery } from '@/core/services/patient'
import { PatientProfileFormData } from '@/features/patient/patient-profile-form'
import { useMeasurementUnitSystem } from '@/hooks/useMeasurementUnitSystem'
import { parseMeasurementValue } from '@/utils/unit-system'
import dayjs from 'dayjs'
import _ from 'lodash'
import { useMemo } from 'react'

export function usePatientInfo({ patient }: { patient?: Patient }) {
  const { unitSystem } = useMeasurementUnitSystem()

  const initialValues: PatientProfileFormData = useMemo(
    () => ({
      agreement: patient?.agreement ?? '',
      secu: patient?.secu ?? '',
      identityConfirmed: patient?.identityConfirmed ?? false,
      insNIR: {
        value: patient?.insNIR?.slice(0, 13) ?? '',
        key: patient?.insNIR?.slice(13) ?? '',
      },
      insOID: patient?.insOID ?? '',
      name: patient?.userInfo.name || '',
      birthName: patient?.userInfo.birthName || '',
      surname: patient?.userInfo.surname || '',
      birthSurname: patient?.userInfo.birthSurname || '',
      email: patient?.email || '',
      nationality: patient?.nationality || '',
      gender: patient?.userInfo.gender || '',
      phone: !patient?.contactInfo?.phone || _.isNaN(parseInt(patient.contactInfo?.phone)) ? '' : patient.contactInfo.phone,
      birthday: !patient?.userInfo.birthday
        ? null
        : (() => {
            try {
              let dateValue = patient.userInfo.birthday

              // If it's a complex object (Day.js, moment, etc.), try to extract the native date
              if (typeof dateValue === 'object' && dateValue !== null) {
                const dateObject = dateValue as any
                if (dateObject.$d) {
                  // Day.js object
                  dateValue = dateObject.$d
                } else if (dateObject._d) {
                  // Moment.js object
                  dateValue = dateObject._d
                } else if (dateObject.toDate && typeof dateObject.toDate === 'function') {
                  // Has toDate method
                  dateValue = dateObject.toDate()
                }
              }

              const date = new Date(dateValue)
              // Check if the date is valid
              if (isNaN(date.getTime())) {
                console.warn('Invalid birthday date:', patient.userInfo.birthday)
                return null
              }
              return date
            } catch (error) {
              console.error('Error parsing birthday:', patient.userInfo.birthday, error)
              return null
            }
          })(),
      birthPlaceCode: patient?.userInfo.birthPlaceCode || '',
      caisse: patient?.caisse || '',
      codeRegime: patient?.codeRegime || '',
      codeGestion: patient?.codeGestion || '',
      weight: parseMeasurementValue('kg', patient?.weight || '', unitSystem),
      height: parseMeasurementValue('cm', patient?.height || '', unitSystem),
      allergy: patient?.allergy || '',
      vaccins: patient?.vaccins || '',
      notes: patient?.notes || '',
      street: patient?.contactInfo?.street || '',
      city: patient?.contactInfo?.city || '',
      cp: _.toString(patient?.contactInfo?.cp || ''),
      country: patient?.contactInfo?.country || '',
      background: patient?.background || '',
      treatment: patient?.treatment || '',
      parents: _.map(patient?.parents, (parent) => ({
        firstname: parent.firstname,
        lastname: parent.lastname,
        email: parent.email || '',
        phone: parent.phone || '',
        city: parent.city || '',
        cp: (parent.cp || '') + '',
      })),
      privateDoctor: patient?.attendingPhysician
        ? {
            firstname: patient.attendingPhysician?.firstname || '',
            lastname: patient.attendingPhysician?.lastname || '',
            email: patient.attendingPhysician?.email || '',
            phone: patient.attendingPhysician?.phone || '',
            cp: patient.attendingPhysician?.cp ? patient.attendingPhysician.cp + '' : '',
            city: patient.attendingPhysician?.city || '',
          }
        : undefined,
      pluginId: patient?.pluginId || '',
      insHistory: patient?.insHistory?.map((history) => ({
        ...history,
        startDate: history.startDate ? dayjs(history.startDate).format('DD/MM/YYYY') : '',
        endDate: history.endDate ? dayjs(history.endDate).format('DD/MM/YYYY') : '',
      })),
      insured: patient?.insured || '',
      insuredRelationship: patient?.insuredRelationship || '',
      insuranceProvider: patient?.insuranceProvider || '',
      primaryInsurance: patient?.primaryInsurance || '',
      secondaryInsurance: patient?.secondaryInsurance || '',
      primaryPolicy: patient?.primaryPolicy || '',
      secondaryPolicy: patient?.secondaryPolicy || '',
      primaryPharmacy: patient?.primaryPharmacy || '',
    }),
    [patient, unitSystem]
  )

  return { initialValues }
}
